import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  AbstractObjectDetailService,
  DocumentDetailService,
  DocumentDtoWithAuthorization,
  DocumentFileDetailService,
  isAuthorizedOperationGranted,
  REF_NUMBER_UNAVAILABLE,
  SUBJECTS_TOOLBAR_BUTTONS_SERVICE,
  SubjectsToolbarButtonsService,
  WITHOUT_REF_NUMBER
} from '|shared';
import {TranslateParser, TranslateService} from '@ngx-translate/core';
import {WaitingDirective, LoadingIndicatorService} from '@icz/angular-essentials';
import {DocumentAuthorizedOperation} from '|api/core';
import {EsslObjectDetailComponent} from '../document-shared/components/essl-object-detail/essl-object-detail.component';
import {IczFormDirective} from '@icz/angular-form-elements';
import {DocumentDetailToolbarComponent} from './components/document-detail-toolbar/document-detail-toolbar.component';
import {RouterOutlet} from '@angular/router';
import {AsyncPipe} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DocumentState} from '|api/commons';
import {ApplicationRoute, DisposedEntitesRoute, DocumentsRoute} from '|ui-essl-core';

@Component({
  selector: 'icz-document-detail',
  templateUrl: './document-detail.component.html',
  styleUrls: ['./document-detail.component.scss'],
  providers: [
    DocumentDetailService,
    {
      provide: AbstractObjectDetailService,
      useExisting: DocumentDetailService,
    },
    {
      provide: DocumentFileDetailService,
      useExisting: DocumentDetailService,
    },
    {
      // This token must be declared for injection here because its implementation relies on DocumentFileDetailService
      //   and it could not be provided directly in SubjectsToolbarButtonsComponent due to cyclic dependencies.
      provide: SUBJECTS_TOOLBAR_BUTTONS_SERVICE,
      useClass: SubjectsToolbarButtonsService,
    },
  ],
  hostDirectives: [IczFormDirective],
  imports: [EsslObjectDetailComponent, WaitingDirective, DocumentDetailToolbarComponent, RouterOutlet, AsyncPipe]
})
export class DocumentDetailComponent implements OnInit {

  protected translateService = inject(TranslateService);
  protected translateParser = inject(TranslateParser);
  protected loadingService = inject(LoadingIndicatorService);
  protected documentDetailService = inject(DocumentDetailService);
  private destroyRef = inject(DestroyRef);

  baseUrlPrefixParts: string[] = [];

  documentNameGeneratorFn = (document: DocumentDtoWithAuthorization) => {
    const hasProfilePermission = isAuthorizedOperationGranted(document.authorization, DocumentAuthorizedOperation.DOCUMENT_SHOW_PROFILE);
    const missingRefNumberText = hasProfilePermission ? WITHOUT_REF_NUMBER : REF_NUMBER_UNAVAILABLE;
    return (
      [`${document.refNumber ?? this.translateService.instant(missingRefNumberText)}`, '-', document.subject!]
    );
  };

  ngOnInit() {
    this.documentDetailService.object$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(object => {
      if (object && object.documentState === DocumentState.DEACTIVATED) {
        this.baseUrlPrefixParts = [ApplicationRoute.DISPOSED_ENTITIES, DisposedEntitesRoute.DEACTIVATED_ENTITY_DETAIL, DocumentsRoute.DOCUMENT];
      } else {
        this.baseUrlPrefixParts = [ApplicationRoute.DOCUMENTS, DocumentsRoute.DOCUMENT];
      }
    });
  }
}
